import { useState, useEffect } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { toast } from "sonner";
import { Eye, EyeSlash, LockKey, Warning, SpinnerGap, CheckCircle, ArrowLeft } from "phosphor-react";
import { PasswordRequirements, checkPasswordStrength, PASSWORD_REQUIREMENTS } from "../components/PasswordRequirements";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [resetComplete, setResetComplete] = useState(false);
  const [error, setError] = useState(null);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [passwordError, setPasswordError] = useState("");

  // No token in URL
  if (!token) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center p-4">
        <div className="fixed inset-0 grain-texture pointer-events-none" />
        <div className="relative z-10 w-full max-w-md">
          <Card className="border-2 border-zinc-200">
            <CardHeader className="text-center">
              <Warning size={48} className="text-amber-500 mx-auto mb-2" />
              <CardTitle className="text-xl">Invalid Reset Link</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-zinc-600">
                This password reset link is invalid or has expired.
              </p>
              <Button
                onClick={() => navigate("/forgot-password")}
                className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
              >
                Request New Reset Link
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setPasswordError("");

    // Client-side pre-check (backend is authoritative)
    const strength = checkPasswordStrength(password);
    if (!strength.meetsRequirements) {
      setPasswordError("Password does not meet requirements.");
      return;
    }

    if (password !== confirmPassword) {
      setError("Passwords do not match");
      return;
    }

    setSubmitting(true);
    try {
      const response = await fetch(`${API}/auth/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, new_password: password }),
      });

      const data = await response.json();

      if (response.ok) {
        setResetComplete(true);
        toast.success("Password reset successfully!");
      } else {
        // Handle PASSWORD_WEAK from backend
        if (data.error === "PASSWORD_WEAK") {
          setPasswordError(data.message || "Password does not meet requirements.");
        } else {
          setError(data.detail || "Failed to reset password");
          toast.error(data.detail || "Failed to reset password");
        }
      }
    } catch (err) {
      console.error("Reset error:", err);
      setError("Failed to reset password. Please try again.");
      toast.error("Failed to reset password. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  // Reset complete state
  if (resetComplete) {
    return (
      <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center p-4">
        <div className="fixed inset-0 grain-texture pointer-events-none" />
        <div className="relative z-10 w-full max-w-md">
          <Card className="border-2 border-zinc-200">
            <CardHeader className="text-center">
              <CheckCircle size={48} className="text-emerald-500 mx-auto mb-2" />
              <CardTitle className="text-xl">Password Updated!</CardTitle>
            </CardHeader>
            <CardContent className="text-center space-y-4">
              <p className="text-zinc-600">
                Your password has been updated successfully. You can now sign in with your new password.
              </p>
              <Button
                onClick={() => navigate("/login")}
                className="bg-[#FF6600] hover:bg-[#CC5200] text-white"
                data-testid="go-to-login-btn"
              >
                Go to Sign In
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Valid token - show password form
  return (
    <div className="min-h-screen bg-[#1A1A1A] flex items-center justify-center p-4">
      <div className="fixed inset-0 grain-texture pointer-events-none" />
      
      <div className="relative z-10 w-full max-w-md">
        <Link
          to="/login"
          className="inline-flex items-center gap-2 text-zinc-400 hover:text-white mb-8 transition-colors"
        >
          <ArrowLeft size={20} />
          <span>Back to login</span>
        </Link>

        <Card className="border-2 border-zinc-200">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-[#FF6600]/10 rounded-full flex items-center justify-center mx-auto mb-4">
              <LockKey size={32} className="text-[#FF6600]" />
            </div>
            <CardTitle className="text-2xl font-bold uppercase">Reset Password</CardTitle>
            <CardDescription>
              Enter your new password below.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6 text-sm">
                {error}
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label htmlFor="password">New Password</Label>
                <div className="relative mt-1">
                  <Input
                    id="password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      setPasswordError("");
                    }}
                    placeholder="Enter new password"
                    className={`pr-10 h-12 border-2 ${passwordError ? 'border-red-400' : 'border-zinc-200'} focus:border-[#FF6600]`}
                    required
                    data-testid="reset-password-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-zinc-600"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? <EyeSlash size={20} /> : <Eye size={20} />}
                  </button>
                </div>
                {/* Password requirements display */}
                <PasswordRequirements password={password} />
                {/* Password error from backend */}
                {passwordError && (
                  <p className="mt-2 text-xs text-red-500" data-testid="reset-password-error">
                    {passwordError}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="confirmPassword">Confirm Password</Label>
                <Input
                  id="confirmPassword"
                  type={showPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Confirm your password"
                  className="mt-1 h-12 border-2 border-zinc-200 focus:border-[#FF6600]"
                  required
                  data-testid="reset-confirm-password-input"
                />
              </div>

              <Button
                type="submit"
                disabled={submitting}
                className="w-full h-12 bg-[#FF6600] hover:bg-[#CC5200] text-white font-heading uppercase tracking-wide"
                data-testid="reset-submit-btn"
              >
                {submitting ? (
                  <>
                    <SpinnerGap size={20} className="animate-spin mr-2" />
                    Updating...
                  </>
                ) : (
                  "Update Password"
                )}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
